import { DxObjectsCache, DxObjectsCacheTypes } from './types';
import { DxObjectsCacheActions } from './actions';
import { MetricsRequestTypes } from '../metrics/types';
import { FunnelsActions, FunnelsRequestTypes } from '../funnels';
import { MetricsActions } from '../metrics';
import {
  InsightRequestTypes,
  MetricsInsightsRequestActions,
  RECENT_INSIGHT_DAYS,
} from '../metricsInsights';
import moment from 'moment';
import { RetentionMetricsRequestTypes } from '../retention/types';
import { RetentionMetricsActions } from '../retention';
import { SegmentRequestTypes } from '../segments/types';
import { SegmentActions } from '../segments';
import { JourneysRequestTypes } from '../journeys';
import { JourneysActions } from '../journeys/actions';
import { HeatmapsActions, HeatmapsRequestTypes } from '../heatmaps';
import { DashboardRequestTypes } from '../dashboards/types';
import { DashboardActions } from '../dashboards';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const CACHE_TIME_TO_LIVE = 60 * 60 * 1000; // 1 hour
const DX_OBJECTS_CACHE_KEY = 'dxObjectsCache';

/**
 * Attempts to convert a raw object into a DxObjectsCache type.
 *
 * @param object raw object that we will attempt to validate
 * @returns a converted DxObjectsCache or undefined if not valid
 */
const validateDxObjectsCacheObject = (object: any): DxObjectsCache | undefined => {
  if (
    object.lastChange !== undefined &&
    (typeof object.lastChange === 'string' || object.lastChange === null) &&
    object.recentInsightIds !== undefined &&
    Array.isArray(object.recentInsightIds) &&
    object.requests !== undefined &&
    typeof object.requests === 'object'
  ) {
    return {
      lastChange: object.lastChange,
      recentInsightIds: new Set<string>(object.recentInsightIds),
      requests: object.requests,
    };
  } else {
    return undefined;
  }
};

/**
 * Loads a DxObjectsCache object out of localstorage. If the value is not
 * valid or defined, we will return undefined. Additionally a TTL check
 * of one hour is done on the object.
 *
 * NOTE: due to not having a clean way to get the OrgId, we have opted to
 * not stamp this with an org. That means a small edge of "creates that are
 * then followed by an org change" will potentially either bust the cache early
 * or fail to bust the cache when needed (even less likely)
 *
 * This reduces the chances of us _not busting the cache_ when we should.
 *
 * TODO - move to backend once pubsub topic is available.
 *
 * @returns a DxObjectsCache object or undefined if invalid
 */
export const loadDxObjectsCacheState: () => DxObjectsCache | undefined = () => {
  try {
    const serialState = localStorage.getItem(DX_OBJECTS_CACHE_KEY);
    if (serialState === null) {
      return undefined;
    }
    const rawState = JSON.parse(serialState);
    const state = validateDxObjectsCacheObject(rawState);
    if (state) {
      const currentTime = Date.now();
      const lastChange = new Date(state.lastChange ?? '').getTime();
      if (currentTime - lastChange <= CACHE_TIME_TO_LIVE) {
        return state;
      }
    }

    localStorage.removeItem(DX_OBJECTS_CACHE_KEY);
    return undefined;
  } catch (err) {
    return undefined;
  }
};

/**
 * Saves the current state to localstorage and returns the original
 * state object. Intended to be used as a helper to advance the
 * reducer's state.
 *
 * @param state state to save
 * @returns unmodified state
 */
const nextState = (state: DxObjectsCache): DxObjectsCache => {
  try {
    const serialState = JSON.stringify({
      lastChange: state.lastChange,
      recentInsightIds: [...state.recentInsightIds], // sets are not supported by stringify
      requests: state.requests,
    });
    localStorage.setItem(DX_OBJECTS_CACHE_KEY, serialState);
  } catch (err) {
    console.log('Failed to save state to storage', err);
  }
  return state;
};

const INITIAL_STATE: DxObjectsCache = {
  lastChange: null,
  recentInsightIds: new Set<string>(),
  requests: {},
};

/**
 * Special case state representing dx object mutation and insights timestamps.
 *
 * This is a (hopefully) temporary solution for Spaces to inform the server of when it needs to
 * skip the DX objects cache due to a known change in FSTA.
 */
export const dxObjectsCacheReducer = (
  state: DxObjectsCache = loadDxObjectsCacheState() ?? INITIAL_STATE,
  action:
    | MetricsActions
    | FunnelsActions
    | RetentionMetricsActions
    | SegmentActions
    | JourneysActions
    | HeatmapsActions
    | DashboardActions
    | MetricsInsightsRequestActions
    | DxObjectsCacheActions
    | BulkRequestActions,
): DxObjectsCache => {
  switch (action.type) {
    // All actions that mutate, star, or archive/delete a DX Object
    case MetricsRequestTypes.SetMetricSuccess:
    case MetricsRequestTypes.ArchiveMetricSuccess:
    case FunnelsRequestTypes.SetFunnelSuccess:
    case FunnelsRequestTypes.ArchiveFunnelSuccess:
    case RetentionMetricsRequestTypes.SetRetentionMetricSuccess:
    case RetentionMetricsRequestTypes.ArchiveRetentionMetricSuccess:
    case SegmentRequestTypes.SetSegmentSuccess:
    case SegmentRequestTypes.ArchiveSegmentSuccess:
    case JourneysRequestTypes.SetJourneySuccess:
    case JourneysRequestTypes.ArchiveJourneySuccess:
    case HeatmapsRequestTypes.SetHeatmapsSuccess:
    case HeatmapsRequestTypes.ArchiveHeatmapSuccess:
    case DashboardRequestTypes.CreateDashboardSuccess:
    case DashboardRequestTypes.UpdateDashboardSuccess:
    case DashboardRequestTypes.DeleteDashboardSuccess:
    case DashboardRequestTypes.BulkDeleteDashboardsSuccess:
    case DashboardRequestTypes.DuplicateDashboardSuccess:
    case DashboardRequestTypes.DashboardTransferOwnerSuccess:
    case DashboardRequestTypes.CreateFromVirtualDashboardSuccess:
    case DxObjectsCacheTypes.InvalidateCache:
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      return nextState({ ...state, lastChange: new Date().toISOString() });
    }

    // insights are a special case that also requires busting the cache.
    // need to record the lastChange when _recent_ insights change as well.
    case InsightRequestTypes.GetInsightsSuccess: {
      const recentThreshold = moment().subtract(RECENT_INSIGHT_DAYS, 'days');
      const recentInsightIds = action.data.insights
        .filter(i => moment(i.createdAt as string).isAfter(recentThreshold))
        .map(i => i.insightId);
      // insights are immutable, so only need to look at ids
      const hasNewRecentInsights = recentInsightIds.some(id => !state.recentInsightIds.has(id));

      // only update when new recent insights come back
      if (hasNewRecentInsights) {
        return nextState({
          ...state,
          // these will get de-duped, and will correctly create a new Set object on change
          recentInsightIds: new Set([...state.recentInsightIds, ...recentInsightIds]),
          lastChange: new Date().toISOString(),
        });
      }
      return state;
    }

    case DxObjectsCacheTypes.MarkResponseTime: {
      return nextState({
        ...state,
        requests: {
          ...state.requests,
          [action.requestKey]: new Date().toISOString(),
        },
      });
    }
  }

  return state;
};
